import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../store/rootReducer";
import ErrorDialog from "../Components/Dialogs/ErrorDialog";
import React from "react";
import {useDialog} from "../providers/DialogProvider";
import {getTotalDownPaymentAmountPaid} from "../utils/utils";
import {
  getSelectedFinanceQuote,
} from "../utils/quoteUtils";
import {hasValue, toDollars, Vehicle} from "@amsanalytics/react-shared-library";
import {useEmployeeRolePermissions} from "../Data/EmployeeRoleData";
import { usePostedAppLock } from './usePostedAppLock';
import {ApplicationDispatch, updateFinanceQuotes} from "../store/application/types";
import {IApplication} from "../Interfaces/Application/IApplication";
import {
  SellingPriceConfirmationDialog
} from "../Components/Views/OrderEditor/SellingPriceConfirmationDialog/SellingPriceConfirmationDialog";

export const useFinanceQuoteSolve = () => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const financeQuotes = currentApp?.financeQuotes;
  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
  const applicationDownPaymentResponses = useSelector((state: AppState) => state.applications.applicationDownPaymentResponses);
  const totalDownPaymentAmountPaid = getTotalDownPaymentAmountPaid(applicationDownPaymentResponses) ?? 0;
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const selectedFinanceLoanClass = lookups.financeLoanClasses.find(flc => flc.id === selectedFinanceQuote?.financeLoanClassId);
  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const { isSectionLocked, isPostedAndLocked, activeUnlock, isReadOnlyLoanSideDeal, lockMessage } = usePostedAppLock();
  const dispatch = useDispatch<ApplicationDispatch>();
  const [openDialog, closeDialog] = useDialog();

  const validateFinanceQuote = (): string[] => {
    const errors: string[] = [];

    if (!selectedFinanceLoanClass) {
      errors.push("You must select a loan class");
    }

    financeQuotes?.filter(afq => !hasValue(afq.deletedDate))?.forEach(afq => {
      if (totalDownPaymentAmountPaid > afq.downPayment) {
        errors.push(`The cash down amount (${toDollars(afq.downPayment)}) cannot be less than the total amount deposited (${toDollars(totalDownPaymentAmountPaid)}) so far`);
      }
    });

    return errors;
  };
  
  const getApplicationWithUpdatedValues = (newVehicle: Vehicle | undefined, userEnteredSellingPrice: number | null): IApplication => {
    if (!currentApp) {
      throw new Error("currentApp is undefined");
    }
    
    return {
      ...currentApp,
      vehicleId: newVehicle?.id ?? currentApp.vehicleId,
      vehicle: newVehicle ?? currentApp.vehicle,
      financeQuotes: currentApp.financeQuotes.map(afq => {
        return {
          ...afq,
          sellingPrice: userEnteredSellingPrice ?? newVehicle?.carPrice ?? afq.sellingPrice
        };
      })
    };
  };

  const handleQuoteSolve = (newVehicle: Vehicle | undefined) => {
    if (!currentApp) {
      return;
    }
    
    if ((!canActInStatus(currentApp.appStatusId ?? "", "Save") && !activeUnlock) || isSectionLocked('Quoter')) {
      const reason = isReadOnlyLoanSideDeal
        ? lockMessage
        : isSectionLocked('Quoter')
        ? "The Quoter section is locked because the application has been finalized and posted. An authorized user must unlock it before editing."
        : "The Quote / Solve button is disabled for your user role(s) in this status";
      openDialog({
        children: (
          <ErrorDialog
            title={"Quote / Solve Disabled"}
            errors={[reason]}
            closeDialog={closeDialog}
          />
        )
      });
    } else {
      const financeQuoteDialogErrors: string[] = validateFinanceQuote();
      if (financeQuoteDialogErrors.length > 0) {
        openDialog({
          children: (
            <ErrorDialog
              title={"Quote / Solve Validation Errors"}
              errors={financeQuoteDialogErrors}
              closeDialog={closeDialog}
            />
          )
        });
      } else if (newVehicle && newVehicle.carPrice <= 0) {
        openDialog({
          children: (
            <SellingPriceConfirmationDialog
              sellingPriceFromSelectedVehicle={newVehicle.carPrice}
              onSave={(newSellingPrice: number) => {
                const updatedCurrentApp: IApplication = getApplicationWithUpdatedValues(newVehicle, newSellingPrice);
                dispatch(updateFinanceQuotes(updatedCurrentApp, newVehicle));
                closeDialog();
              }}
              onClose={closeDialog}
            />
          )
        });
      } else {
        const updatedCurrentApp: IApplication = getApplicationWithUpdatedValues(newVehicle, null);
        dispatch(updateFinanceQuotes(updatedCurrentApp, newVehicle));
      }
    }
  };

  return [handleQuoteSolve];
}